import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { formatDate } from "../lib/format.js";
import { PageSpinner, EmptyState } from "../components/ui.js";
import { QuickLogModal, type QuickLogType } from "../components/QuickLogModal.js";
import { IconBody } from "../components/icons.js";

interface Measurement { valueIn: number; entryDate: string; customLabel: string | null }
interface Photo { id: string; photoDate: string; pose: string; weightLb: number | null; notes: string | null }

const SITE_LABELS: Record<string, string> = {
  waist: "Waist", chest: "Chest", shoulders: "Shoulders", neck: "Neck",
  left_arm: "Left arm", right_arm: "Right arm", hips: "Hips",
  left_thigh: "Left thigh", right_thigh: "Right thigh", left_calf: "Left calf", right_calf: "Right calf"
};
const PHYSIQUE_AREAS: [string, string][] = [
  ["chest", "Chest"], ["shoulders", "Shoulders"], ["arms", "Arms"],
  ["back", "Back"], ["abs", "Abs"], ["legs", "Legs"], ["waist", "Smaller waist"]
];

export function BodyPage() {
  const [latestBySite, setLatestBySite] = useState<Record<string, Measurement>>({});
  const [weight, setWeight] = useState<number | null>(null);
  const [goals, setGoals] = useState<Record<string, number>>({});
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [modal, setModal] = useState<QuickLogType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [m, w, g, p] = await Promise.all([
        api.get<{ latestBySite: Record<string, Measurement> }>("/api/body/measurements"),
        api.get<{ trend: { smoothedCurrent: number | null } }>("/api/body/weight"),
        api.get<{ goals: { area: string; priority: number }[] }>("/api/body/physique-goals"),
        api.get<{ photos: Photo[] }>("/api/photos")
      ]);
      setLatestBySite(m.latestBySite ?? {});
      setWeight(w.trend.smoothedCurrent);
      setGoals(Object.fromEntries(g.goals.map((x) => [x.area, x.priority])));
      setPhotos(p.photos);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load body data.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function saveGoal(area: string, priority: number) {
    const next = { ...goals, [area]: priority };
    setGoals(next);
    await api.put("/api/body/physique-goals", { goals: Object.entries(next).map(([a, p]) => ({ area: a, priority: p })) }).catch(() => undefined);
  }

  if (loading) {
    return <div><div className="page-header"><h1>Body</h1></div>{error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}</div>;
  }

  const sites = Object.keys(latestBySite);

  return (
    <div>
      <div className="page-header">
        <h1>Body</h1>
        <p className="subtitle">Measurements, your physique priorities, and private progress photos.</p>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="stack">
        {/* Measurements */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Measurements</span>
            <div className="row">
              <button className="btn btn-sm" onClick={() => setModal("weight")}>+ Weight</button>
              <button className="btn btn-sm" onClick={() => setModal("measurement")}>+ Measurement</button>
            </div>
          </div>
          <div className="stat-grid">
            <div className="stat"><div className="stat-label">Weight (trend)</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{weight != null ? `${weight} lb` : "—"}</div></div>
            {sites.map((s) => (
              <div className="stat" key={s}>
                <div className="stat-label">{SITE_LABELS[s] ?? latestBySite[s]!.customLabel ?? s.replace("custom:", "")}</div>
                <div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{latestBySite[s]!.valueIn}"</div>
              </div>
            ))}
          </div>
          {sites.length === 0 && <p className="dim text-sm mt-4" style={{ marginBottom: 0 }}>No measurements yet — add your waist to start tracking your shape, not just the scale.</p>}
        </div>

        {/* Physique priorities */}
        <div className="card">
          <div className="card-header"><span className="card-title">What are we building?</span></div>
          <p className="dim text-xs" style={{ marginTop: -4 }}>Set how much each area matters. This shapes training priorities (appearance outcomes depend partly on genetics — this guides the plan, it doesn't promise a result).</p>
          <div className="stack mt-4">
            {PHYSIQUE_AREAS.map(([area, label]) => (
              <div className="row-between" key={area}>
                <span>{label}</span>
                <div className="row" style={{ gap: 4 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" aria-label={`${label} priority ${n}`} onClick={() => saveGoal(area, n)}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, color: (goals[area] ?? 0) >= n ? "var(--accent-cyan)" : "var(--surface-4)" }}>
                      ★
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress photos */}
        <PhotoSection photos={photos} onChanged={load} />
      </div>

      {modal && <QuickLogModal type={modal} onClose={() => setModal(null)} onLogged={load} />}
    </div>
  );
}

function PhotoSection({ photos, onChanged }: { photos: Photo[]; onChanged: () => void }) {
  const [pose, setPose] = useState("front");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pose", pose);
      fd.append("photoDate", new Date().toISOString().slice(0, 10));
      fd.append("file", file);
      const res = await fetch("/api/photos", { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.message ?? "Upload failed.");
      }
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function remove(id: string) {
    await api.del(`/api/photos/${id}`).catch(() => undefined);
    onChanged();
  }

  // Before/Current for the front pose.
  const front = photos.filter((p) => p.pose === "front").sort((a, b) => a.photoDate.localeCompare(b.photoDate));
  const showCompare = front.length >= 2;

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Progress photos</span></div>
      <p className="dim text-xs" style={{ marginTop: -4 }}>Private to you — stored securely and never shared or publicly linked.</p>

      <div className="row wrap mt-4" style={{ gap: "var(--space-2)" }}>
        <select className="select" style={{ maxWidth: 140 }} value={pose} onChange={(e) => setPose(e.target.value)}>
          <option value="front">Front</option>
          <option value="side">Side</option>
          <option value="back">Back</option>
          <option value="custom">Custom</option>
        </select>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
        <button className="btn btn-sm btn-primary" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? "Uploading…" : "Add photo"}
        </button>
      </div>
      {error && <div className="alert alert-danger mt-4">{error}</div>}

      {showCompare && (
        <div className="mt-6">
          <div className="nav-section-label" style={{ padding: "0 0 var(--space-2)" }}>Front · Then → Now</div>
          <div className="photo-compare">
            <figure><img src={`/api/photos/${front[0]!.id}/file`} alt="First front photo" /><figcaption className="dim text-xs">{formatDate(front[0]!.photoDate)}{front[0]!.weightLb ? ` · ${front[0]!.weightLb} lb` : ""}</figcaption></figure>
            <figure><img src={`/api/photos/${front[front.length - 1]!.id}/file`} alt="Latest front photo" /><figcaption className="dim text-xs">{formatDate(front[front.length - 1]!.photoDate)}{front[front.length - 1]!.weightLb ? ` · ${front[front.length - 1]!.weightLb} lb` : ""}</figcaption></figure>
          </div>
        </div>
      )}

      {photos.length === 0 ? (
        <EmptyState icon={<IconBody />} title="No photos yet">Front, side, and back photos make change visible in a way the scale can't.</EmptyState>
      ) : (
        <div className="photo-grid mt-6">
          {photos.map((p) => (
            <figure className="photo-card" key={p.id}>
              <img src={`/api/photos/${p.id}/file`} alt={`${p.pose} ${p.photoDate}`} loading="lazy" />
              <figcaption>
                <span className="badge badge-muted">{p.pose}</span>
                <span className="dim text-xs">{formatDate(p.photoDate)}</span>
                <button className="btn btn-sm btn-ghost" onClick={() => remove(p.id)}>Delete</button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
